import React, { useState } from 'react';

import { EyeIcon, EyeOffIcon } from 'lucide-react';

interface ResetPasswordFormProps {
	password: string;
	setPassword: (password: string) => void;
	confirmPassword: string;
	setConfirmPassword: (password: string) => void;
	loading: boolean;
	loadingLookup: boolean;
	error: string;
	successMessage: string;
	userEmail: string;
	onSubmit: (e: React.FormEvent) => void;
	onBackToLogin: () => void;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
	password,
	setPassword,
	confirmPassword,
	setConfirmPassword,
	loading,
	loadingLookup,
	error,
	successMessage,
	userEmail,
	onSubmit,
	onBackToLogin,
}) => {
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	return (
		<form className="space-y-6" onSubmit={onSubmit}>
			{successMessage && (
				<div className="rounded-md bg-green-50 p-4">
					<div className="flex">
						<div className="ml-3">
							<h3 className="text-sm font-medium text-green-800">
								{successMessage}
							</h3>
						</div>
					</div>
				</div>
			)}

			{!successMessage ? (
				<>
					<div>
						<label className="block text-sm font-medium text-gray-700">
							Email
						</label>
						<div className="mt-1">
							<input
								type="email"
								value={userEmail}
								readOnly
								className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700 sm:text-sm"
							/>
						</div>
					</div>

					<div>
						<label
							htmlFor="password"
							className="block text-sm font-medium text-gray-700"
						>
							New password
						</label>
						<div className="mt-1 relative">
							<input
								id="password"
								name="password"
								type={showPassword ? 'text' : 'password'}
								autoComplete="new-password"
								required
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
								placeholder="Enter new password"
							/>
							<button
								type="button"
								onClick={() => setShowPassword((v) => !v)}
								className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
								aria-label={showPassword ? 'Hide password' : 'Show password'}
							>
								{showPassword ? (
									<EyeIcon size={20} />
								) : (
									<EyeOffIcon size={20} />
								)}
							</button>
						</div>
					</div>

					<div>
						<label
							htmlFor="confirmPassword"
							className="block text-sm font-medium text-gray-700"
						>
							Confirm password
						</label>
						<div className="mt-1 relative">
							<input
								id="confirmPassword"
								name="confirmPassword"
								type={showConfirmPassword ? 'text' : 'password'}
								autoComplete="new-password"
								required
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
								placeholder="Re-enter new password"
							/>
							<button
								type="button"
								onClick={() => setShowConfirmPassword((v) => !v)}
								className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
								aria-label={
									showConfirmPassword ? 'Hide password' : 'Show password'
								}
							>
								{showConfirmPassword ? (
									<EyeIcon size={20} />
								) : (
									<EyeOffIcon size={20} />
								)}
							</button>
						</div>
					</div>

					{error && (
						<div className="rounded-md bg-red-50 p-4">
							<div className="flex">
								<div className="ml-3">
									<h3 className="text-sm font-medium text-red-800">{error}</h3>
								</div>
							</div>
						</div>
					)}

					<div className="flex items-center justify-between">
						<button
							type="submit"
							disabled={loading || loadingLookup}
							className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{loading ? 'Resetting password...' : 'Reset password'}
						</button>
					</div>
				</>
			) : null}

			<div className="mt-2 text-center">
				<button
					type="button"
					className="text-sm text-gray-600 hover:text-gray-800"
					onClick={onBackToLogin}
				>
					Back to sign in
				</button>
			</div>
		</form>
	);
};

export default ResetPasswordForm;
